import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import "react-datepicker/dist/react-datepicker.css";
import { enGB, es, pt } from "date-fns/locale";
import { format, isValid, parseISO } from "date-fns";
import { getReservationSums } from "../actions/restaurantActions";
const StyledDatePickerWrapper = styled.div`
  width: 100%;
  font-size: 13px;

  .react-datepicker {
    width: 100%;
    font-family: inherit;
    font-size: 13px;
    border: none;
    border-radius: 10px;
    box-shadow: none;
    padding: 0;
    background-color: #ffffff;
    color: #493b3d;
  }

  .react-datepicker__month-container {
    width: 100%;
  }

  .react-datepicker__header {
    background: #f3f4f4 !important;
    border-bottom: 0;
    border-radius: 10px 10px 0 0;
    padding: 0 8px 8px !important;
    margin: 0 !important;
  }

  .react-datepicker__current-month {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    margin: 0;
    padding: 0 46px;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    color: #4b5553;
    text-align: center;
  }

  .react-datepicker__day-names {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    align-items: center;
    height: 26px;
    margin: 0 !important;
    padding: 0 !important;
  }

  .react-datepicker__day-name {
    display: grid !important;
    place-items: center;
    width: 100% !important;
    height: 26px;
    margin: 0 !important;
    padding: 0 !important;
    color: #7c8684;
    font-weight: 600;
    font-size: 11px;
    line-height: 1;
  }

  .react-datepicker__month {
    margin: 9px 8px 8px !important;
  }

  .react-datepicker__week {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .react-datepicker__day {
    display: grid;
    place-items: center;
    width: calc(100% - 4px);
    max-width: 44px;
    min-width: 0;
    height: 54px;
    line-height: 1;
    justify-self: center;
    font-weight: 600;
    font-size: 14px;
    margin: 2px 0;
    border-radius: 9px;
    position: relative;
    color: #4b3f41;
  }

  .react-datepicker__day--selected {
    background-color: #ee754c !important;
    color: white !important;
    border-radius: 9px;
  }

  /* Keyboard preselection is not the user's selected date. Override the
     app-wide orange keyboard-selected rule only inside this calendar. */
  .react-datepicker__day--keyboard-selected:not(.react-datepicker__day--selected) {
    background: transparent !important;
    color: #4b3f41 !important;
  }

  .react-datepicker__day:focus-visible {
    outline: 2px solid #6f8f8b;
    outline-offset: -2px;
  }

  .react-datepicker__day:hover {
    background-color: #f3f4f4 !important;
    opacity: 1 !important;
    transform: none !important;
    box-shadow: none !important;
    color: #4b3f41 !important;
  }

  .react-datepicker__day--selected:hover {
    background-color: #d9633e !important;
    color: #ffffff !important;
  }

  .react-datepicker__day--today:not(.react-datepicker__day--selected) {
    outline: 1px solid #99b0ad;
    outline-offset: -1px;
    font-weight: 750;
  }

  .react-datepicker__day--outside-month {
    visibility: hidden;
  }

  .react-datepicker__navigation--previous,
  .react-datepicker__navigation--next {
    background-color: #ffffff;
    border: none;
    border-radius: 7px;
    box-shadow: 0 1px 4px rgba(65, 86, 82, 0.08);
    width: 28px;
    height: 28px;
    position: absolute;
    top: 7px;
    display: grid;
    place-items: center;
    cursor: pointer;
    padding: 0;
    z-index: 2;
    transition: background-color 0.15s ease;
  }

  .react-datepicker__navigation--previous {
    left: 8px;
  }

  .react-datepicker__navigation--next {
    right: 8px;
  }

  .react-datepicker__navigation-icon {
    position: absolute;
    inset: 0;
    top: 0;
    width: 100%;
    height: 100%;
    font-size: 0;
  }

  .react-datepicker__navigation-icon::before {
    top: 50%;
    left: 50%;
    right: auto;
    width: 7px;
    height: 7px;
    margin-top: -4px;
    margin-left: -4px;
    border-color: #707a78;
    border-width: 2px 2px 0 0;
  }

  .react-datepicker__navigation--previous:hover,
  .react-datepicker__navigation--next:hover {
    background-color: #fff0ea;
    box-shadow: none;
  }

  .day-content {
    display: grid;
    grid-template-rows: 18px 26px;
    align-content: center;
    row-gap: 2px;
    place-items: center;
    width: 100%;
    height: 100%;
    position: relative;
    line-height: 1;
  }

  .day-counts {
    display: grid;
    grid-template-rows: repeat(2, 13px);
    justify-items: center;
    width: 100%;
    min-width: 0;
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    line-height: 13px;
    white-space: nowrap;
  }

  .day-count--pending {
    grid-row: 1;
    color: #b74b29;
  }

  .day-count--accepted {
    grid-row: 2;
    color: #426f68;
  }

  .day-warning {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #dc6843;
  }

  .react-datepicker__day--selected .day-count {
    color: #ffffff;
    background: transparent;
  }

  .react-datepicker__day--selected .day-warning {
    background: #ffffff;
  }
`;
const Container = styled.div`
  width: 100%;
  max-width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
function CustomDatePicker({
  handleTimeChange,
  date,
  handleCloseDateModal,
  restaurant_id
}) {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const dateLocale = language === "pt" ? pt : language === "es" ? es : enGB;
  const dispatch = useDispatch();
  const { reservationSums } = useSelector(state => state.reservationSums);
  // Initialize the picker with the actual filter, not today followed by an
  // effect. That also keeps its initial keyboard focus on the selected day.
  const selectedDate = useMemo(() => {
    const parsedDate = typeof date === "string" ? parseISO(date) : null;
    return isValid(parsedDate) ? parsedDate : new Date();
  }, [date]);
  const handleVisibleMonthDispatch = dateInMonth => {
    const middleOfMonth = new Date(dateInMonth.getFullYear(), dateInMonth.getMonth(), 15);
    dispatch(getReservationSums(restaurant_id, format(middleOfMonth, "yyyy-MM-dd")));
  };
  const highlightedDates = useMemo(() => {
    const datesWithSums = {};
    (reservationSums?.date_summary || []).forEach(dateSummary => {
      // "all" already includes every shift; summing shifts again duplicates totals.
      const totals = dateSummary.all || {};
      datesWithSums[dateSummary.date] = {
        sum1: Number(totals.accepted || 0),
        sum2: Number(totals.pending_or_edited || 0),
        warning: Number(totals.warnings || 0) > 0
      };
    });
    return datesWithSums;
  }, [reservationSums]);
  const renderDayContents = (day, dayDate) => {
    const { sum1 = 0, sum2 = 0, warning = false } =
      highlightedDates[format(dayDate, "dd/MM/yyyy")] || {};
    const hasActivity = sum1 !== 0 || sum2 !== 0 || warning;
    return <div
        className="day-content"
        title={hasActivity ? `${sum2} pendentes · ${sum1} aceites` : undefined}
      >
        <span className="day-number">{day}</span>
        {warning && <span className="day-warning" aria-hidden="true" />}
        <span className="day-counts">
          {sum2 !== 0 && <span className="day-count day-count--pending">{sum2}</span>}
          {sum1 !== 0 && <span className="day-count day-count--accepted">{sum1}</span>}
        </span>
      </div>;
  };
  return <StyledDatePickerWrapper>
      <Container>
        <DatePicker
          selected={selectedDate}
          onChange={nextDate => {
            if (!isValid(nextDate)) return;
            handleTimeChange(format(nextDate, "yyyy-MM-dd"));
          }}
          onSelect={handleCloseDateModal}
          onMonthChange={handleVisibleMonthDispatch}
          inline
          renderDayContents={renderDayContents}
          locale={dateLocale}
          weekStartsOn={1}
        />
      </Container>
    </StyledDatePickerWrapper>;
}
export default CustomDatePicker;
